import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { api, NeuralChatApiError } from "../api/client";
import { useNeuralChatAuth } from "../contexts/AuthContext";
import { CurrentMember, Room } from "../types";
import { getInitials, roleLabel } from "../utils/routes";
import {
  ArchivedLabel, Avatar, Brand, BrandCopy, BrandMark, CreateRoomForm, Field, Input,
  LogoutButton, MemberCopy, MemberSummary, RoomButton, RoomButtonCopy, RoomHash,
  RoomList, Sidebar, SidebarAction, SidebarBackdrop, SidebarBottom, SidebarSection,
  SectionHeading, SidebarNav, SidebarNavLink, Textarea, TinyButton, ErrorNotice, UnreadDot,
} from "../styles";

type Props = {
  rooms: Room[];
  member: CurrentMember;
  selectedRoomId?: string;
  open: boolean;
  loading: boolean;
  error: string;
  unreadRoomIds: string[];
  onClose: () => void;
  onRefresh: () => Promise<void>;
};

export const RoomSidebar: React.FC<Props> = ({ rooms, member, selectedRoomId, open, loading, error, unreadRoomIds, onClose, onRefresh }) => {
  const navigate = useNavigate();
  const auth = useNeuralChatAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [form, setForm] = useState({ slug: "", name: "", description: "" });
  const canCreate = member.kind === "HUMAN" && member.role === "CEO" && member.is_active;

  const createRoom = async (event: React.FormEvent) => {
    event.preventDefault();
    if (creating) return;
    const slug = form.slug.trim().toLowerCase();
    const name = form.name.trim();
    if (!slug || !name) { setCreateError("채널 이름과 slug를 입력해 주세요."); return; }
    setCreating(true); setCreateError("");
    try {
      const room = await api.createRoom({ slug, name, description: form.description.trim() });
      setForm({ slug: "", name: "", description: "" });
      setCreateOpen(false);
      await onRefresh();
      navigate(`/neuralchat/rooms/${room.id}`);
      onClose();
    } catch (reason) {
      setCreateError(reason instanceof NeuralChatApiError ? reason.message : "채널을 만들지 못했습니다.");
    } finally { setCreating(false); }
  };

  const logout = async () => {
    await auth.logout();
    navigate("/neuralchat/login", { replace: true });
  };

  return (
    <>
      <SidebarBackdrop type="button" aria-label="사이드바 닫기" $open={open} onClick={onClose} />
      <Sidebar $open={open} aria-label="NeuralChat 채널 목록">
        <Brand>
          <BrandMark aria-hidden="true">NN</BrandMark>
          <BrandCopy><strong>NeuralChat</strong><span>internal team space</span></BrandCopy>
          <SidebarAction type="button" aria-label="사이드바 닫기" onClick={onClose}>×</SidebarAction>
        </Brand>
        <SidebarNav aria-label="NeuralChat 업무 메뉴">
          <SidebarNavLink as={NavLink} to="/neuralchat" end onClick={onClose}>채팅</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/opportunities" onClick={onClose}>영업 기회</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/projects" onClick={onClose}>프로젝트</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/approvals" onClick={onClose}>결재함</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/mail" onClick={onClose}>메일</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/mail/outbox" onClick={onClose}>Mail Outbox</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/sales" onClick={onClose}>Sales Cockpit</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/workflows" onClick={onClose}>Workflow</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/meetings" onClick={onClose}>회의</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/reports" onClick={onClose}>일일 보고</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/action-center" onClick={onClose}>Action center</SidebarNavLink>
          <SidebarNavLink as={NavLink} to="/neuralchat/delivery" onClick={onClose}>Delivery Center</SidebarNavLink>
        </SidebarNav>
        <SidebarSection>
          <SectionHeading><span>Rooms</span>{canCreate && <SidebarAction type="button" aria-label="채널 만들기" onClick={() => setCreateOpen((value) => !value)}>＋</SidebarAction>}</SectionHeading>
          {createOpen && canCreate && (
            <CreateRoomForm onSubmit={createRoom}>
              <Field><span>Slug</span><Input value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} autoComplete="off" /></Field>
              <Field><span>Name</span><Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoComplete="off" /></Field>
              <Field><span>Description</span><Textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></Field>
              {createError && <ErrorNotice role="alert">{createError}</ErrorNotice>}
              <TinyButton type="submit" disabled={creating}>{creating ? "생성 중…" : "채널 생성"}</TinyButton>
            </CreateRoomForm>
          )}
          {loading && !rooms.length ? <MutedSidebar>채널을 불러오는 중…</MutedSidebar> : null}
          {error && rooms.length ? <MutedSidebar>{error}</MutedSidebar> : null}
          <RoomList>
            {rooms.map((room) => (
              <RoomButton key={room.id} type="button" $selected={room.id === selectedRoomId} $archived={room.is_archived} onClick={() => { navigate(`/neuralchat/rooms/${room.id}`); onClose(); }}>
                <RoomHash aria-hidden="true">#</RoomHash><RoomButtonCopy>{room.name}</RoomButtonCopy>{room.is_archived && <ArchivedLabel>archived</ArchivedLabel>}{unreadRoomIds.includes(room.id) && <UnreadDot aria-label="읽지 않은 멘션" />}
              </RoomButton>
            ))}
          </RoomList>
          {!loading && !rooms.length && <MutedSidebar>아직 접근 가능한 채널이 없습니다.</MutedSidebar>}
        </SidebarSection>
        <SidebarBottom>
          <MemberSummary><Avatar $kind={member.kind}>{getInitials(member.display_name)}</Avatar><MemberCopy><strong>{member.display_name}</strong><span>{roleLabel(member.role)} · {member.kind}</span></MemberCopy></MemberSummary>
          <LogoutButton type="button" onClick={logout}>로그아웃</LogoutButton>
        </SidebarBottom>
      </Sidebar>
    </>
  );
};

const MutedSidebar = ({ children }: React.PropsWithChildren) => <p style={{ padding: "8px", color: "#94a3b8", fontSize: 12 }}>{children}</p>;
